import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { RouteEnum, auditApi, useGotoRoute } from 'App';
import { fullDateTime, DialogTitle } from '@shared';
import { FinishAuditPropsType } from './FinishAuditDialog.types';

export function FinishAuditDialog(props: FinishAuditPropsType) {
  const { isModalOpen, onCloseModal, useStatuses, useComments, auditUuid } = props;
  const { statuses, updateStatuses, isStatusesLoading } = useStatuses;
  const [comments, addComment, isCommentsLoading] = useComments;
  const [commentValue, setCommentValue] = useState('');
  const needsReview = statuses?.needsReview;
  const goToRoute = useGotoRoute(RouteEnum.AuditQueue);

  const { mutate: finishAudit } = useMutation({
    mutationFn: () => auditApi.finishAudit(auditUuid),
    onSuccess: () => goToRoute(),
  });

  const handleSendComment = () => {
    addComment(commentValue);
    setCommentValue('');
  };

  return (
    <Dialog open={isModalOpen} onClose={onCloseModal} closeAfterTransition fullWidth maxWidth='sm'>
      <DialogTitle fontSize={24} title='Finish Audit' onClose={onCloseModal} />
      <DialogContent sx={{ maxHeight: 300 }} dividers>
        <List>
          {comments && comments.length < 1 && <Typography>No Comments</Typography>}
          {isCommentsLoading && <Typography>comments are loading</Typography>}
          {!isCommentsLoading &&
            comments?.map((comment) => (
              <ListItem key={comment.uuid} disablePadding>
                <ListItemText
                  id='comment'
                  primary={
                    <Stack direction='row' gap={3} alignItems='baseline'>
                      <Typography variant='faded'>
                        {comment.firstName + ' ' + comment.lastName}
                      </Typography>
                      <Typography variant='faded' fontSize={12}>
                        {fullDateTime(comment.timestamp)}
                      </Typography>
                    </Stack>
                  }
                  secondary={<Typography sx={{ opacity: 0.8 }}>{comment.comment}</Typography>}
                />
              </ListItem>
            ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ padding: 0 }}>
        <TextField
          sx={{ margin: '16px 24px' }}
          fullWidth
          label='Write comment'
          multiline
          rows={4}
          value={commentValue}
          onChange={(e) => setCommentValue(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end' sx={{ alignItems: 'flex-start' }}>
                <Button disabled={!commentValue} variant='contained' onClick={handleSendComment}>
                  Write Comment
                </Button>
              </InputAdornment>
            ),
          }}
        />
      </DialogActions>

      <DialogActions sx={{ p: '8px 24px', justifyContent: 'space-between' }}>
        <Button variant='outlined' onClick={onCloseModal}>
          Cancel
        </Button>
        <Button
          color={needsReview ? 'warning' : 'primary'}
          variant={needsReview ? 'contained' : 'outlined'}
          onClick={() =>
            updateStatuses({ auditUuid, statuses: { ...statuses, needsReview: !needsReview } })
          }
          disabled={isStatusesLoading}
        >
          {needsReview ? 'Under Review' : 'For Review'}
        </Button>
        <Button variant='contained' onClick={() => finishAudit()}>
          Finish Audit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
